import { useNavigateWithSearch } from 'Modulor/hooks';
import { useMethodGroupingContext } from 'providers/MethodGroupingProvider';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import type { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import { sortByArrangement } from 'store/formatters';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  depositMethodsSelector,
  paygroupCategoriesSelector,
  setCurrentMethod as setDepositCurrentMethod,
} from 'store/slices/deposit';
import { paymentFlowSelector, setSelectedPayGroupId } from 'store/slices/global';
import { isOnMethodRouteSelector } from 'store/slices/methodForm';
import {
  isShowGroupingMethodsInCarouselSelector,
  selectedCategorySelector,
  setUserCategorySelection,
} from 'store/slices/methodGrouping';
import { isSubmitFormActiveSelector } from 'store/slices/submitForm';
import {
  payoutGroupCategoriesSelector,
  setCurrentMethod as setWithdrawalCurrentMethod,
  withdrawalMethodsSelector,
} from 'store/slices/withdrawal';
import { DISABLED_CATEGORY_KEY, isDepositFlowType, isMethodAvailable } from 'utils';

import CategoryTabs from './CategoryTabs';

/**
 * MethodGroupingController — single place for category tabs rendering.
 * Reads shared data from MethodGroupingProvider context.
 * Handles category switching: updates carousel methods, navigates to first method.
 */
const MethodGroupingController: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigateWithSearch();
  const { pathname } = useLocation();
  const flowType = useAppSelector(paymentFlowSelector);
  const isDeposit = isDepositFlowType(flowType);
  // Store selectors for carousel sync on category change
  const paygroupCategories = useAppSelector(isDeposit ? paygroupCategoriesSelector : payoutGroupCategoriesSelector);
  const methods = useAppSelector(isDeposit ? depositMethodsSelector : withdrawalMethodsSelector);
  // Visibility selectors
  const selectedCategory = useAppSelector(selectedCategorySelector);
  const isShowGroupingMethodsInCarousel = useAppSelector(isShowGroupingMethodsInCarouselSelector);
  const isMethodGroupingActive = useAppSelector(isOnMethodRouteSelector);
  const isSubmitFormActive = useAppSelector(isSubmitFormActiveSelector);
  // Shared data from context (no duplicate useMethodGrouping call)
  const { carouselTabs, handleCategorySelect, getUserSelectedGroup } = useMethodGroupingContext();

  const navigateToMethodList = useCallback(() => {
    const basePath = isDeposit ? '/deposit' : '/withdrawal';
    if (pathname === basePath) {
      return;
    }
    navigate(basePath, {
      replace: true,
    });
  }, [isDeposit, pathname, navigate]);

  const handleCategoryChange = useCallback(
    (categoryName: string) => {
      handleCategorySelect(categoryName);

      if (!isShowGroupingMethodsInCarousel || categoryName === DISABLED_CATEGORY_KEY) {
        navigateToMethodList();
        return;
      }

      // Carousel methods are computed by derived selector (groupingCarouselMethodsSelector).
      // Here we only handle method selection and navigation.
      const categoryConf = paygroupCategories?.categories?.find((cat) => cat.category_name === categoryName);
      if (!categoryConf) return;

      const categoryMethods = (methods as (UIPaymentGroups | UIPayoutGroups)[])
        .filter((m) => categoryConf.pay_group_ids.includes(m.id) && isMethodAvailable(m))
        .sort(sortByArrangement);

      const savedMethodId = getUserSelectedGroup(categoryName);
      const savedMethod = savedMethodId ? categoryMethods.find((m) => m.id === savedMethodId) : null;
      const methodToSelect = savedMethod ?? categoryMethods[0];

      if (methodToSelect) {
        dispatch(setSelectedPayGroupId(methodToSelect.id));
        dispatch(
          setUserCategorySelection({
            category_name: categoryName,
            payGroupId: methodToSelect.id,
          }),
        );

        if (isDeposit) {
          dispatch(setDepositCurrentMethod(methodToSelect as UIPaymentGroups));
        } else {
          dispatch(setWithdrawalCurrentMethod(methodToSelect as UIPayoutGroups));
        }

        navigate(`/${flowType}/${methodToSelect.id}`, {
          replace: true,
        });
      }
    },
    [
      handleCategorySelect,
      isShowGroupingMethodsInCarousel,
      navigateToMethodList,
      paygroupCategories,
      methods,
      dispatch,
      getUserSelectedGroup,
      isDeposit,
      flowType,
      navigate,
    ],
  );

  const shouldShowTabs = isMethodGroupingActive && carouselTabs.length > 0 && !isSubmitFormActive;

  if (!shouldShowTabs) {
    return null;
  }

  return (
    <CategoryTabs list={carouselTabs} onSelect={handleCategoryChange} selectedTab={selectedCategory || undefined} />
  );
};

export default MethodGroupingController;
